import { useEffect, useRef, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { BusinessInfoPanel } from '@/components/business-info-panel';
import { ExternalWebsiteCard } from '@/components/external-website-card';
import { api } from '@/lib/api';
import { locationRoutes } from '@/lib/route-map';
import { deriveBusinessInfoSeed, takeAttachedBusiness } from '@/lib/attach-context';
import type { ExistingBusinessInfoFields } from '@/lib/attach-context';
import type { SiteStatus } from '@/lib/types';

// =====================================================================
// FoundationStep (WP3 Task 8) — the wizard's Foundation step BODY (spec
// §8 step 1). WizardStep chrome (title, continue/skip buttons, step N of 4)
// is supplied by the shell (Task 11); this component only renders what goes
// inside it. Two things happen here:
//
//  1. A one-time business-info seed, IF the owner arrived via WP2's
//     demo-attach flow (Task 12 stashes the attached business through
//     attach-context.ts's `stashAttachedBusiness`). We take() the stash,
//     GET the current canonical business-info, and — only for fields that
//     are still empty — PATCH the derived subset ONCE, all BEFORE
//     `BusinessInfoPanel` mounts. The panel then loads the seeded values
//     through its own GET; there is no second form and no bypass of the
//     single write path (`PATCH /api/sites/:id/business-info`).
//
//  2. The "have a site already?" choice (spec §8): continue with the draft
//     (only for `site.status === 'draft'`), connect an existing external
//     site (mounts `ExternalWebsiteCard` verbatim — DNS TXT proof,
//     site-lifecycle #19), or start fresh. No choice is required to
//     continue; domain/mailbox setup is deliberately absent here (spec §8:
//     "falls to the overview path"). One-primary-CTA-per-step demotion
//     (2026-08-17): these three used to render as three always-visible
//     full cards stacked below the NAP form — a second competing decision
//     on a screen whose one job is "confirm your business info". They now
//     live inside a single collapsed `<details>` disclosure ("Have a site
//     already?"), closed by default — same components, same copy, same
//     behaviors/links verbatim, just demoted to optional and out of the
//     way until asked for.
//
//  3. The NAP form itself (`BusinessInfoPanel`) now receives `registerSave`
//     — its own inner "Save" button is gone (see business-info-panel.tsx's
//     header); the wizard's single bottom "save and continue" performs the
//     NAP PATCH before advancing.
//
// StrictMode double-mounts effects in dev (WP2-F4 lesson) — `seedRanRef`
// guards the seed effect so a second mount can't fire a second PATCH; the
// take()/read-and-clear in attach-context.ts gives a second natural
// idempotency layer even without the ref (a second take() would see
// nothing left to seed), but the ref also prevents a redundant GET.
//
// `onDirty` has no dedicated hook to attach to on `BusinessInfoPanel` or
// `ExternalWebsiteCard` (both mount verbatim, per this task's brief, with
// no onChange-style prop) — it fires on any click anywhere inside this
// step's body. That errs toward calling it too eagerly rather than missing
// a real edit, which is the safe direction for a "did the owner touch this
// step" signal; the wizard shell (Task 11) is free to treat it as a coarse
// "engaged with this step" flag.
//
// SITE-SCOPED TAKE (Task 12 closed the gap flagged here): `takeAttachedBusiness`
// now takes this step's own `siteId` and only returns a business stashed for
// THAT site — an owner who attaches a business for site A, abandons the
// wizard before reaching this step, then later starts onboarding a
// DIFFERENT site B in the same tab, no longer gets site B seeded with site
// A's stashed business (the mismatched stash is discarded, not applied).
// =====================================================================

interface BusinessInfoGetResponse {
  businessInfo: ExistingBusinessInfoFields;
}

interface SiteStatusResponse {
  site: { status: SiteStatus };
}

type PreviewUrlResponse = { url: string } | { disabled: true };

export function FoundationStep({
  siteId,
  onDirty,
  registerSave,
}: {
  siteId: string;
  onDirty?: () => void;
  // Uniform step-body contract (WP3 Task 11, carried item 1): the shell
  // composes all four steps through the same { siteId, registerSave? }
  // shape. One-primary-CTA-per-step (2026-08-17): this is now forwarded to
  // `BusinessInfoPanel`, which registers the real NAP save — the shell's
  // Continue is no longer a no-op here. `ExternalWebsiteCard`'s own
  // DNS-proof flow (save URL / check DNS / remove) stays on its own
  // explicit buttons, unrelated to this registration — connecting an
  // external site is a distinct, optional decision, not this step's save.
  registerSave?: (save: () => Promise<void>) => void;
}) {
  // Canonical tenant URLs carry the Business id; these screens are only
  // ever rendered inside one, so it comes from the router, not a prop.
  const businessId = useBusinessId() ?? '';
  const seedRanRef = useRef(false);
  const [seeded, setSeeded] = useState(false);
  const [status, setStatus] = useState<SiteStatus | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);


  // 1. One-time seed, guarded against StrictMode's double-mount.
  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !siteId) return;
    if (seedRanRef.current) return;
    seedRanRef.current = true;

    (async () => {
      const stashed = takeAttachedBusiness(siteId);
      if (!stashed) {
        setSeeded(true);
        return;
      }
      try {
        const res = await api.get<BusinessInfoGetResponse>(
          locationApi(businessId, siteId, 'business-info'),
        );
        const seed = deriveBusinessInfoSeed(res.businessInfo, stashed);
        if (seed) {
          await api.patch(locationApi(businessId, siteId, 'business-info'), seed);
        }
      } catch {
        // Seeding is best-effort: BusinessInfoPanel still loads fine (just
        // empty for whatever didn't get seeded) if this fails, and the
        // owner can always fill fields in by hand.
      } finally {
        setSeeded(true);
      }
      // Run once per mount only -- `siteId` is stable for the lifetime of
      // this step (one site being onboarded), and re-running this on a
      // siteId change would risk a second seed PATCH racing the first.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    })();
  }, []);

  // 2. Site status, to decide whether the draft card is shown.
  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !siteId) return;
    let cancelled = false;
    api
      .get<SiteStatusResponse>(locationApi(businessId, siteId, ''))
      .then((res) => {
        if (cancelled) return;
        setStatus(res.site.status);
      })
      .catch(() => {
        // Non-fatal: the draft card just stays hidden until this resolves.
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, siteId]);

  // 3. Preview link for the draft card, only once we know it's a draft.
  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !siteId) return;
    if (status !== 'draft') return;
    let cancelled = false;
    api
      .get<PreviewUrlResponse>(locationApi(businessId, siteId, 'preview-url'))
      .then((res) => {
        if (cancelled) return;
        if ('url' in res) setPreviewUrl(res.url);
      })
      .catch(() => {
        // Non-fatal: the card still renders, just without a preview link.
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, status, siteId]);

  return (
    <div className="space-y-6" onClickCapture={() => onDirty?.()}>
      {seeded ? (
        <BusinessInfoPanel siteId={siteId} {...(registerSave ? { registerSave } : {})} />
      ) : (
        // aria-live wrapper (same convention as competitor-intel-cards.tsx /
        // aeo-audit-card.tsx): a screen-reader user gets an announcement for
        // this step's own seed-loading window instead of silence followed by
        // an unannounced DOM swap once BusinessInfoPanel mounts.
        <div aria-live="polite">
          <p role="status" className="sr-only">
            Loading your business info…
          </p>
          <div className="h-64 animate-pulse rounded-lg bg-muted" />
        </div>
      )}

      {/* One-primary-CTA-per-step demotion (2026-08-17, see file header):
          the three "have a site already" choices are now ONE collapsed
          disclosure instead of three always-visible cards — same
          components/copy/behavior verbatim, closed by default so they
          don't compete with the NAP form above for attention. */}
      <details className="group rounded-lg border border-border-subtle bg-card">
        <summary className="flex cursor-pointer list-none items-start justify-between gap-3 px-6 py-4 [&::-webkit-details-marker]:hidden">
          <span>
            <span className="text-sm font-semibold text-foreground">Have a site already?</span>
            <span className="mt-0.5 block text-sm text-text-secondary">
              keep building the draft, connect a site you already have, or start fresh — nothing
              here is required
            </span>
          </span>
          <ChevronDown
            className="mt-0.5 h-4 w-4 shrink-0 text-text-tertiary transition-transform duration-[var(--duration-fast)] group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>

        <div className="space-y-4 border-t border-border-subtle px-6 py-4">
          {status === 'draft' ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
              <span className="font-medium text-foreground">Continue with this draft</span>
              <Link
                to={locationRoutes.website(businessId, siteId)}
                className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
              >
                Keep building
              </Link>
              {previewUrl ? (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
                >
                  Preview your draft
                </a>
              ) : null}
            </div>
          ) : null}

          <ExternalWebsiteCard siteId={siteId} />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
            <span className="font-medium text-foreground">Start fresh</span>
            <Link
              to={locationRoutes.website(businessId, siteId)}
              className="font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
            >
              Start building
            </Link>
          </div>
        </div>
      </details>
    </div>
  );
}
